import { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Papa from 'papaparse';
import * as XLSX from 'xlsx';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  Chip,
  Checkbox,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  CircularProgress,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DownloadIcon from '@mui/icons-material/Download';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import { type OperatorRecord } from './OperatorImporterView';
import {
  OPERATOR_HEADERS,
  REQUIRED_HEADERS,
  TEMPLATE_SAMPLE_ROW,
  validateHeaders,
} from './operatorImporterUtils';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';

interface ParsedRow {
  id: string;
  rowNum: number;
  firstName: string;
  lastName: string;
  isActive: boolean;
  role: string;
  skills: string[];
  experience: string;
  errors: string[];
}

interface Props {
  open: boolean;
  onClose: () => void;
  onImport: (rows: OperatorRecord[]) => Promise<void>;
}

const TABLE_COLS = [
  'firstName',
  'lastName',
  'isActive',
  'role',
  'skills',
  'experience',
];

const parseBoolean = (v: unknown): boolean | null => {
  const s = String(v ?? '')
    .trim()
    .toLowerCase();
  if (['true', '1', 'yes', 'active'].includes(s)) return true;
  if (['false', '0', 'no', 'inactive'].includes(s)) return false;
  return null;
};

const downloadTemplate = () => {
  const headerKeys: Array<keyof typeof TEMPLATE_SAMPLE_ROW> = [
    'FIRST_NAME',
    'LAST_NAME',
    'IS_ACTIVE',
    'ROLE',
    'SKILLS',
    'EXPERIENCE',
  ];
  const headers = headerKeys.map((h) => OPERATOR_HEADERS[h]);
  const sampleValues = headerKeys.map((h) => {
    const v = TEMPLATE_SAMPLE_ROW[h] ?? '';
    return v.includes(',') ? `"${v}"` : v;
  });
  const csv = [headers.join(','), sampleValues.join(',')].join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'operator_import_template.csv';
  a.click();
  URL.revokeObjectURL(url);
};

const downloadErrors = (errorRows: ParsedRow[]) => {
  const headers = [
    ...REQUIRED_HEADERS.map((h) => OPERATOR_HEADERS[h]),
    OPERATOR_HEADERS.ROLE,
    OPERATOR_HEADERS.SKILLS,
    OPERATOR_HEADERS.EXPERIENCE,
    'errors',
  ];
  const rows = errorRows.map((r) =>
    [
      r.firstName,
      r.lastName,
      String(r.isActive),
      r.role,
      r.skills.length > 0 ? `"${r.skills.join(',')}"` : '',
      r.experience,
      `"${r.errors.join('; ')}"`,
    ].join(',')
  );
  const csv = [headers.join(','), ...rows].join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'operator_import_errors.csv';
  a.click();
  URL.revokeObjectURL(url);
};

const validateRow = (
  raw: Record<string, unknown>,
  rowNum: number
): ParsedRow => {
  const errs: string[] = [];
  const str = (v: unknown) => String(v ?? '').trim();

  const u: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(raw)) {
    u[k.trim().toUpperCase().replace(/ /g, '_')] = v;
  }

  const firstName = str(u.FIRSTNAME ?? u.FIRST_NAME ?? u['FIRST NAME']);
  const lastName = str(u.LASTNAME ?? u.LAST_NAME ?? u['LAST NAME']);
  const isActiveRaw = u.ISACTIVE ?? u.IS_ACTIVE ?? u['IS ACTIVE'];
  const role = str(u.ROLE);
  const skillsRaw = str(u.SKILLS ?? '');
  const skills = skillsRaw
    ? skillsRaw
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean)
    : [];
  const experience = str(u.EXPERIENCE ?? '');

  if (!firstName) errs.push('firstName is required');
  if (!lastName) errs.push('lastName is required');

  const isActiveParsed = parseBoolean(isActiveRaw);
  if (isActiveParsed === null) errs.push('isActive must be true/false');

  return {
    id: `row-${rowNum}`,
    rowNum,
    firstName,
    lastName,
    isActive: isActiveParsed ?? false,
    role,
    skills,
    experience,
    errors: errs,
  };
};

export const ImportOperatorModal = ({
  open,
  onClose,
  onImport,
}: Props): JSX.Element => {
  const { t } = useTranslation();
  const [parsedRows, setParsedRows] = useState<ParsedRow[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [isDragging, setIsDragging] = useState(false);
  const [fileName, setFileName] = useState('');
  const [headerError, setHeaderError] = useState<string[] | null>(null);
  const [importing, setImporting] = useState(false);

  useEffect(() => {
    if (!open) {
      setParsedRows([]);
      setSelectedIds(new Set());
      setFileName('');
      setHeaderError(null);
    }
  }, [open]);

  const validRows = parsedRows.filter((r) => r.errors.length === 0);
  const errorRows = parsedRows.filter((r) => r.errors.length > 0);

  const applyRows = (rows: ParsedRow[]) => {
    setParsedRows(rows);
    setSelectedIds(
      new Set(rows.filter((r) => r.errors.length === 0).map((r) => r.id))
    );
  };

  const processFile = useCallback((file: File) => {
    setFileName(file.name);
    setHeaderError(null);
    const ext = file.name.split('.').pop()?.toLowerCase();
    if (ext === 'csv') {
      Papa.parse<Record<string, unknown>>(file, {
        header: true,
        skipEmptyLines: true,
        complete: (result) => {
          const fileHeaders = result.meta.fields ?? [];
          const { valid, missing } = validateHeaders(fileHeaders);
          if (!valid) {
            setHeaderError(missing);
            return;
          }
          applyRows(result.data.map((row, i) => validateRow(row, i + 2)));
        },
      });
    } else {
      const reader = new FileReader();
      reader.onload = (e) => {
        const wb = XLSX.read(e.target?.result, { type: 'array' });
        const sheet = wb.Sheets[wb.SheetNames[0]];
        const json = XLSX.utils.sheet_to_json<Record<string, unknown>>(sheet, {
          defval: '',
        });
        const fileHeaders = json.length > 0 ? Object.keys(json[0]) : [];
        const { valid, missing } = validateHeaders(fileHeaders);
        if (!valid) {
          setHeaderError(missing);
          return;
        }
        applyRows(json.map((row, i) => validateRow(row, i + 2)));
      };
      reader.readAsArrayBuffer(file);
    }
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragging(false);
      const file = e.dataTransfer.files[0];
      if (file) processFile(file);
    },
    [processFile]
  );

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) processFile(file);
    e.target.value = '';
  };

  const toggleRow = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleAll = (checked: boolean) => {
    setSelectedIds(checked ? new Set(validRows.map((r) => r.id)) : new Set());
  };

  const handleImport = async () => {
    const toImport: OperatorRecord[] = validRows
      .filter((r) => selectedIds.has(r.id))
      .map((r) => ({
        id: crypto.randomUUID(),
        operatorName: `${r.firstName} ${r.lastName}`,
        firstName: r.firstName,
        lastName: r.lastName,
        role: r.role,
        isActive: r.isActive,
        pin: '',
        assetVin: '',
        assetBumperNumber: '',
        assetId: null,
        skills: r.skills,
        experience: r.experience !== '' ? r.experience : undefined,
      }));
    setImporting(true);
    try {
      await onImport(toImport);
    } finally {
      setImporting(false);
    }
  };

  const handleClose = () => {
    setParsedRows([]);
    setSelectedIds(new Set());
    setFileName('');
    setHeaderError(null);
    onClose();
  };

  const handleRetry = () => {
    setParsedRows([]);
    setHeaderError(null);
    setFileName('');
  };

  const allSelected =
    validRows.length > 0 && selectedIds.size === validRows.length;
  const someSelected = selectedIds.size > 0 && !allSelected;

  const colLabel = (col: string): string => {
    const map: Record<string, string> = {
      firstName: t('operator_importer_field_first_name').replace(' *', ''),
      lastName: t('operator_importer_field_last_name').replace(' *', ''),
      isActive: t('operator_importer_col_active'),
      role: t('operator_importer_col_role'),
      skills: t('operator_importer_field_skills'),
      experience: t('operator_importer_field_experience'),
    };
    return map[col] ?? col;
  };

  const fmtCell = (col: string, row: ParsedRow): string => {
    if (col === 'skills') {
      return row.skills.length > 0 ? row.skills.join(', ') : '—';
    }
    const val = row[col as keyof ParsedRow];
    if (val === null || val === undefined || val === '') return '—';
    if (typeof val === 'boolean')
      return val
        ? t('operator_importer_chip_yes')
        : t('operator_importer_chip_no');
    if (Array.isArray(val)) return val.length > 0 ? val.join(', ') : '—';
    return String(val);
  };

  const palette = clientThemePalette[CLIENT_THEME].light;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={parsedRows.length > 0 ? 'md' : headerError ? 'md' : 'sm'}
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          bgcolor: 'background.paper',
          border: '1px solid',
          borderColor: (t: { palette: { primary: { main: string } } }) =>
            alpha(t.palette.primary.main, 0.15),
          backgroundImage: 'none',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          px: 3,
          py: 2,
        }}
      >
        <Typography variant="h6" fontWeight={700}>
          {t('operator_importer_import_title')}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Button
            size="small"
            startIcon={<DownloadIcon />}
            onClick={downloadTemplate}
            variant="outlined"
            sx={{ borderRadius: '8px', fontWeight: 600, fontSize: '0.78rem' }}
          >
            {t('operator_importer_template_btn')}
          </Button>
          <IconButton onClick={handleClose} size="small" sx={{ ml: 0.5 }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ px: 3, pt: 0, pb: 2 }}>
        {headerError ? (
          <Box
            sx={{
              borderRadius: 3,
              border: '1px solid',
              borderColor: (t) => alpha(t.palette.error.main, 0.35),
              bgcolor: (t) => alpha(t.palette.error.main, 0.04),
              p: 3,
              textAlign: 'center',
            }}
          >
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: '50%',
                mx: 'auto',
                mb: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: (t) => alpha(t.palette.error.main, 0.12),
                border: '1px solid',
                borderColor: (t) => alpha(t.palette.error.main, 0.3),
              }}
            >
              <Typography sx={{ fontSize: 26 }}>⚠️</Typography>
            </Box>
            <Typography
              variant="subtitle1"
              fontWeight={700}
              color="error.main"
              gutterBottom
            >
              {t('operator_importer_missing_cols')}
            </Typography>
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 0.75,
                justifyContent: 'center',
                mb: 3,
              }}
            >
              {headerError.map((col) => (
                <Box
                  key={col}
                  sx={{
                    px: 1.5,
                    py: 0.4,
                    borderRadius: '6px',
                    bgcolor: (t) => alpha(t.palette.error.main, 0.1),
                    border: '1px solid',
                    borderColor: (t) => alpha(t.palette.error.main, 0.3),
                  }}
                >
                  <Typography
                    variant="caption"
                    fontWeight={700}
                    color="error.main"
                  >
                    {col}
                  </Typography>
                </Box>
              ))}
            </Box>
            <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'center' }}>
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={downloadTemplate}
                sx={{ borderRadius: '8px', fontWeight: 600 }}
              >
                {t('operator_importer_template_btn')}
              </Button>
              <Button
                variant="contained"
                onClick={handleRetry}
                sx={{ borderRadius: '8px', fontWeight: 600 }}
              >
                {t('operator_importer_import_btn')}
              </Button>
            </Box>
          </Box>
        ) : parsedRows.length === 0 ? (
          <Box
            onDragOver={(e) => {
              e.preventDefault();
              setIsDragging(true);
            }}
            onDragLeave={() => {
              setIsDragging(false);
            }}
            onDrop={handleDrop}
            onClick={() => {
              document.getElementById('operator-file-input')?.click();
            }}
            sx={{
              border: '2px dashed',
              borderColor: (t) =>
                isDragging
                  ? t.palette.primary.main
                  : alpha(t.palette.primary.main, 0.35),
              borderRadius: 3,
              py: 6,
              px: 4,
              textAlign: 'center',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              bgcolor: (t) =>
                isDragging
                  ? alpha(t.palette.primary.main, 0.08)
                  : alpha(t.palette.primary.main, 0.03),
              '&:hover': {
                borderColor: 'primary.main',
                bgcolor: (t) => alpha(t.palette.primary.main, 0.06),
              },
            }}
          >
            <input
              id="operator-file-input"
              type="file"
              accept=".csv,.xlsx,.xls"
              style={{ display: 'none' }}
              onChange={handleFileInput}
            />
            <Box
              sx={{
                width: 72,
                height: 72,
                borderRadius: '50%',
                mx: 'auto',
                mb: 2.5,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: (t) => alpha(t.palette.primary.main, 0.12),
                border: '2px solid',
                borderColor: (t) => alpha(t.palette.primary.main, 0.3),
                boxShadow: (t) =>
                  `0 0 32px ${alpha(t.palette.primary.main, 0.18)}`,
              }}
            >
              <CloudUploadIcon sx={{ fontSize: 34, color: 'primary.main' }} />
            </Box>
            <Typography
              variant="h6"
              fontWeight={700}
              color="text.primary"
              gutterBottom
            >
              {t('operator_importer_import_title')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
              {t('operator_importer_import_drop')}
            </Typography>
            <Box
              sx={{ display: 'flex', gap: 1, justifyContent: 'center', mb: 3 }}
            >
              {['.CSV', '.XLSX', '.XLS'].map((fmt) => (
                <Box
                  key={fmt}
                  sx={{
                    px: 1.5,
                    py: 0.35,
                    borderRadius: '6px',
                    bgcolor: (t) => alpha(t.palette.primary.main, 0.1),
                    border: '1px solid',
                    borderColor: (t) => alpha(t.palette.primary.main, 0.22),
                  }}
                >
                  <Typography
                    variant="caption"
                    fontWeight={700}
                    color="text.secondary"
                  >
                    {fmt}
                  </Typography>
                </Box>
              ))}
            </Box>
            <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'center' }}>
              <Button
                variant="outlined"
                sx={{ borderRadius: '8px', px: 3, fontWeight: 600 }}
                onClick={(e) => {
                  e.stopPropagation();
                  document.getElementById('operator-file-input')?.click();
                }}
              >
                {t('operator_importer_import_btn')}
              </Button>
              <Button
                variant="outlined"
                startIcon={<DownloadIcon sx={{ fontSize: 16 }} />}
                sx={{ borderRadius: '8px', px: 3, fontWeight: 600 }}
                onClick={(e) => {
                  e.stopPropagation();
                  downloadTemplate();
                }}
              >
                {t('operator_importer_template_btn')}
              </Button>
            </Box>
          </Box>
        ) : (
          <Box>
            {/* File info bar */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                mb: 2,
                py: 1.5,
                px: 2,
                borderRadius: 2,
                bgcolor: (t) => alpha(t.palette.primary.main, 0.05),
                border: '1px solid',
                borderColor: (t) => alpha(t.palette.divider, 0.5),
              }}
            >
              <InsertDriveFileOutlinedIcon
                fontSize="small"
                sx={{ color: 'text.secondary' }}
              />
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ flex: 1 }}
                noWrap
              >
                {fileName}
              </Typography>
              <Chip
                label={`${validRows.length} valid`}
                color="success"
                size="small"
              />
              {errorRows.length > 0 && (
                <Chip
                  label={`${errorRows.length} error${
                    errorRows.length > 1 ? 's' : ''
                  }`}
                  color="error"
                  size="small"
                />
              )}
              <Button
                size="small"
                sx={{
                  color: 'text.secondary',
                  ml: 0.5,
                  borderRadius: '6px',
                  fontSize: '0.75rem',
                }}
                onClick={() => {
                  setParsedRows([]);
                  setFileName('');
                }}
              >
                {t('operator_importer_cancel')}
              </Button>
            </Box>

            {/* Error rows */}
            {errorRows.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    mb: 0.75,
                  }}
                >
                  <Typography
                    variant="caption"
                    fontWeight={700}
                    color="error.main"
                    sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}
                  >
                    {`${errorRows.length} error${
                      errorRows.length > 1 ? 's' : ''
                    }`}
                  </Typography>
                  <Button
                    size="small"
                    startIcon={<DownloadIcon sx={{ fontSize: 14 }} />}
                    onClick={() => {
                      downloadErrors(errorRows);
                    }}
                    variant="outlined"
                    sx={{
                      fontSize: '0.72rem',
                      fontWeight: 600,
                      borderRadius: '6px',
                      color: 'error.main',
                      borderColor: (t) => alpha(t.palette.error.main, 0.4),
                      '&:hover': {
                        bgcolor: (t) => alpha(t.palette.error.main, 0.06),
                      },
                    }}
                  >
                    {t('operator_importer_result_download')}
                  </Button>
                </Box>
                <TableContainer
                  sx={{
                    maxHeight: 200,
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: (t) => alpha(t.palette.error.main, 0.3),
                    bgcolor: (t) => alpha(t.palette.error.main, 0.02),
                  }}
                >
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        {[
                          '#',
                          ...TABLE_COLS.map(colLabel),
                          t('operator_importer_result_failed'),
                        ].map((col) => (
                          <TableCell
                            key={col}
                            sx={{
                              bgcolor: 'background.paper',
                              fontWeight: 700,
                              fontSize: '0.72rem',
                              color: 'error.main',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {col}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {errorRows.map((row) => (
                        <TableRow key={row.id}>
                          <TableCell
                            sx={{
                              color: 'text.secondary',
                              fontSize: '0.75rem',
                            }}
                          >
                            {row.rowNum}
                          </TableCell>
                          {TABLE_COLS.map((col) => (
                            <TableCell
                              key={col}
                              sx={{ fontSize: '0.75rem', whiteSpace: 'nowrap' }}
                            >
                              {fmtCell(col, row)}
                            </TableCell>
                          ))}
                          <TableCell
                            sx={{
                              fontSize: '0.72rem',
                              color: 'error.main',
                              maxWidth: 200,
                            }}
                          >
                            {row.errors.join(' · ')}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}

            {/* Valid rows */}
            {validRows.length > 0 && (
              <Box>
                <Typography
                  variant="caption"
                  fontWeight={700}
                  color="text.secondary"
                  sx={{
                    mb: 1,
                    display: 'block',
                    textTransform: 'uppercase',
                    letterSpacing: 0.5,
                  }}
                >
                  {t('operator_importer_import_ready', {
                    count: selectedIds.size,
                  })}
                </Typography>
                <TableContainer
                  sx={{
                    maxHeight: 300,
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: (t) => alpha(t.palette.divider, 0.6),
                    bgcolor: (t) => alpha(t.palette.primary.main, 0.02),
                  }}
                >
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell
                          padding="checkbox"
                          sx={{ bgcolor: 'background.paper' }}
                        >
                          <Checkbox
                            checked={allSelected}
                            indeterminate={someSelected}
                            onChange={(e) => {
                              toggleAll(e.target.checked);
                            }}
                            size="small"
                          />
                        </TableCell>
                        {TABLE_COLS.map((col) => (
                          <TableCell
                            key={col}
                            sx={{
                              bgcolor: 'background.paper',
                              fontWeight: 700,
                              fontSize: '0.75rem',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {colLabel(col)}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {validRows.map((row) => (
                        <TableRow
                          key={row.id}
                          selected={selectedIds.has(row.id)}
                          hover
                          onClick={() => {
                            toggleRow(row.id);
                          }}
                          sx={{ cursor: 'pointer' }}
                        >
                          <TableCell padding="checkbox">
                            <Checkbox
                              checked={selectedIds.has(row.id)}
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                              }}
                              onChange={() => {
                                toggleRow(row.id);
                              }}
                            />
                          </TableCell>
                          {TABLE_COLS.map((col) => (
                            <TableCell
                              key={col}
                              sx={{ fontSize: '0.75rem', whiteSpace: 'nowrap' }}
                            >
                              {fmtCell(col, row)}
                            </TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        {parsedRows.length > 0 && (
          <Button
            variant="contained"
            onClick={() => {
              void handleImport();
            }}
            disabled={selectedIds.size === 0 || importing}
            startIcon={
              importing ? (
                <CircularProgress size={14} color="inherit" />
              ) : undefined
            }
            sx={{ borderRadius: '8px', fontWeight: 600, px: 3 }}
          >
            {t('operator_importer_import_btn')} ({selectedIds.size})
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};
